/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useState } from "react";
import {
  Box,
  Heading,
  Text,
  VStack,
  Input,
  HStack,
  Button,
  Spinner,
  Center,
  Divider,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../hooks/useTheme";
import PageContainer from "../../components/PageContainer";
import UnifiedCard from "../../components/UnifiedCard";
import UnifiedChart from "../../components/UnifiedChart";
import { formatNumber } from "utils/formatNumber";
export default function RevenuesReport() {
  const { i18n } = useTranslation();
  const { isArabic, colors, dir, textAlign } = useTheme();

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const baseUrl = (process.env.REACT_APP_API_BASE_URL || "https://madar-api.madar.iq/api").replace(/\/+$/, '');

  const fetchReport = async () => {
    if (!fromDate || !toDate) {
      // Show validation message
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");

      // Use tenantId=-1 to get all payments for all tenants (platform-wide report)
      const tenantId = -1;

      // Format dates to ISO format (YYYY-MM-DDTHH:mm:ss) or just date string
      // The API expects date-time format, so we'll append time if needed
      const fromDateTime = fromDate.includes('T') ? fromDate : `${fromDate}T00:00:00`;
      const toDateTime = toDate.includes('T') ? toDate : `${toDate}T23:59:59`;

      const url = `${baseUrl}/PaymentsReport?tenantId=${tenantId}&fromDate=${encodeURIComponent(fromDateTime)}&toDate=${encodeURIComponent(toDateTime)}`;

      const res = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Authorization": token ? `Bearer ${token}` : "",
        },
      });

      if (!res.ok) {
        throw new Error(`Failed to fetch report: ${res.status}`);
      }

      const result = await res.json();
      setData(Array.isArray(result) ? result : []);
    } catch (err) {
      console.error("Error fetching report:", err);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const total = data.reduce(
    (sum, r) => sum + (Number(r.transactionAmount) || 0),
    0
  );

  const dayDiff =
    fromDate && toDate
      ? Math.max(
        1,
        Math.ceil(
          (new Date(toDate) - new Date(fromDate)) / (1000 * 60 * 60 * 24)
        ) + 1
      )
      : 0;

  const dailyAvg = dayDiff ? total / dayDiff : 0;
  const weeklyTotal = dailyAvg * 7;
  const monthlyTotal = dailyAvg * 30;

  // Group data by date to show daily totals
  const chartData = (() => {
    if (!data.length) return [];

    const groupedByDate = {};
    data.forEach((r) => {
      const date = new Date(r.transactionDate).toISOString().split("T")[0];
      if (!groupedByDate[date]) {
        groupedByDate[date] = 0;
      }
      groupedByDate[date] += Number(r.transactionAmount) || 0;
    });

    // Convert to array and sort by date
    return Object.entries(groupedByDate)
      .map(([date, amount]) => ({
        date: date,
        amount: amount,
      }))
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  })();

  return (
    <PageContainer title={isArabic ? "تقرير شامل عن الإيرادات" : "Comprehensive Revenue Report"}>
      <UnifiedCard mb={8} hoverable={false}>
        <VStack spacing={4}>
          <HStack spacing={3} flexWrap="wrap" justify="center" w="100%">
            <Input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              placeholder={isArabic ? "من تاريخ" : "From Date"}
              w={{ base: "100%", sm: "180px" }}
              bg={colors.inputBg}
              color={colors.textColor}
              borderColor={colors.inputBorder}
              _focus={{ borderColor: colors.inputFocus, boxShadow: `0 0 0 1px ${colors.inputFocus}` }}
              _hover={{ borderColor: colors.inputFocus }}
              _dark={{ bg: colors.inputBg, color: colors.textColor }}
            />
            <Input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              placeholder={isArabic ? "إلى تاريخ" : "To Date"}
              w={{ base: "100%", sm: "180px" }}
              bg={colors.inputBg}
              color={colors.textColor}
              borderColor={colors.inputBorder}
              _focus={{ borderColor: colors.inputFocus, boxShadow: `0 0 0 1px ${colors.inputFocus}` }}
              _hover={{ borderColor: colors.inputFocus }}
              _dark={{ bg: colors.inputBg, color: colors.textColor }}
            />
            <Button
              colorScheme="purple"
              onClick={fetchReport}
              px={6}
              borderRadius="lg"
              w={{ base: "100%", sm: "auto" }}
              isLoading={loading}
            >
              {isArabic ? "عرض التقرير" : "View Report"}
            </Button>
          </HStack>
          <Text fontSize="sm" color={colors.subText} textAlign="center">
            {isArabic ? "تقرير شامل عن إيرادات المنصة حسب الفترة المحددة" : "Platform-wide revenue report for selected period"}
          </Text>
        </VStack>
      </UnifiedCard>

      {loading ? (
        <Center py={10}>
          <Spinner color={colors.mainColor} size="xl" />
        </Center>
      ) : (
        <UnifiedCard>
          {data.length ? (
            <VStack spacing={5}>
              <Text color={colors.textColor} fontWeight="bold">
                {isArabic ? "عدد العمليات:" : "Transactions:"} {data.length}
              </Text>
              <Divider />

              <Text color={colors.mainColor} fontSize="lg" fontWeight="bold">
                💰 {isArabic ? "إجمالي الإيرادات:" : "Total Revenue:"}{" "}
                {formatNumber(Math.round(total))} {isArabic ? "د.ع" : "IQD"}
              </Text>

              <Text color={colors.subText}>
                📅 {isArabic ? "عدد الأيام:" : "Days:"} {dayDiff}
              </Text>

              <Text color={colors.subText}>
                📊 {isArabic ? "المتوسط اليومي:" : "Daily Average:"}{" "}
                {formatNumber(Math.round(dailyAvg))} {isArabic ? "د.ع" : "IQD"}
              </Text>

              <Text color={colors.subText}>
                🗓️ {isArabic ? "إجمالي أسبوعي تقريبي:" : "Approx. Weekly Total:"}{" "}
                {formatNumber(Math.round(weeklyTotal))}{isArabic ? "د.ع" : "IQD"}
              </Text>

              <Text color={colors.subText}>
                📆 {isArabic ? "إجمالي شهري تقريبي:" : "Approx. Monthly Total:"}{" "}
                {formatNumber(Math.round(monthlyTotal))} {isArabic ? "د.ع" : "IQD"}
              </Text>

              <Divider />
              <Text color={colors.subText}>
                {isArabic ? "الفترة من" : "Period from"} {fromDate}{" "}
                {isArabic ? "إلى" : "to"} {toDate}
              </Text>

              <Box mt={6} w="100%">
                <UnifiedChart
                  type="bar"
                  data={chartData}
                  dataKey="amount"
                  nameKey="date"
                  height={300}
                  gradient={true}
                />
              </Box>
            </VStack>
          ) : (
            <Text color={colors.subText} textAlign="center">
              {isArabic ? "لا توجد بيانات" : "No data"}
            </Text>
          )}
        </UnifiedCard>
      )}
    </PageContainer>
  );
}
